import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Box, Chip, Avatar, List, ListItem, ListItemAvatar, ListItemText, Divider, IconButton, Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/PersonOutline';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/StarOutline';
import api from '../api';

const ContractorAssignment = ({ open, onClose, requestId, currentAssignment, onAssignmentUpdate }) => {
  const [contractors, setContractors] = useState([]);
  const [suggestedContractors, setSuggestedContractors] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchContractors();
      if (requestId) {
        fetchSuggestedContractors();
      }
      if (currentAssignment) {
        setSelectedContractor(currentAssignment.contractorId || '');
        setAssignmentNotes(currentAssignment.notes || '');
      } else {
        setSelectedContractor('');
      }
    }
  }, [open, requestId, currentAssignment]);

  const fetchContractors = async () => {
    try {
      const response = await api.get('/api/contractors');
      setContractors(response.data);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  };

  const fetchSuggestedContractors = async () => {
    if (!requestId) return;
    setSuggestedLoading(true);
    try {
      const response = await api.get(`/api/maintenance-requests/${requestId}/suggested-contractors`);
      setSuggestedContractors(response.data || []);
    } catch (error) {
      console.error('Error fetching suggested contractors:', error);
      setSuggestedContractors([]);
    } finally {
      setSuggestedLoading(false);
    }
  };

  const assignContractor = async (contractor) => {
    if (!contractor) return;

    setLoading(true);
    try {
      const assignmentData = {
        assignedContractorId: contractor.id,
        contractorId: contractor.id,
        contractorName: contractor.name || 'Unknown Contractor',
        assignmentNotes: assignmentNotes,
        assignedAt: new Date(),
        status: 'assigned'
      };

      await api.put(`/api/maintenance-requests/${requestId}`, assignmentData);

      onAssignmentUpdate(assignmentData);
      onClose();
    } catch (error) {
      console.error('Error assigning contractor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedContractor) return;
    const contractor = contractors.find(c => c.id === selectedContractor);
    await assignContractor(contractor);
  };

  const handleAssignSuggested = async (contractor) => {
    await assignContractor(contractor);
  };

  const handleUnassign = async () => {
    setLoading(true);
    try {
      await api.put(`/api/maintenance-requests/${requestId}`, {
        assignedContractorId: null, // Backend expects this field
        contractorId: null, // Keep for backward compatibility
        contractorName: null,
        assignmentNotes: '',
        assignedAt: null,
        status: 'submitted'
      });
      
      onAssignmentUpdate(null);
      onClose();
    } catch (error) {
      console.error('Error unassigning contractor:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedContractorData = contractors.find(c => c.id === selectedContractor);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Assign Contractor
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close assignment dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {suggestedContractors.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Suggested Contractors
            </Typography>
            {suggestedLoading ? (
              <Typography variant="body2" color="text.secondary">Loading suggestions...</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {suggestedContractors.map((contractor, index) => (
                  <Box
                    key={contractor.id}
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: index === 0 ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      bgcolor: index === 0 ? 'grey.100' : 'transparent'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={index === 0 ? 600 : 400}>
                          {contractor.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(contractor.specialties || []).join(', ') || contractor.category || 'General'}
                          {contractor.matchScore != null && ` • Match: ${contractor.matchScore}`}
                        </Typography>
                      </Box>
                      {index === 0 && (
                        <Chip icon={<StarIcon sx={{ fontSize: 14 }} />} label="Best match" size="small" color="primary" />
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AssignmentIcon />}
                      onClick={() => handleAssignSuggested(contractor)}
                      disabled={loading}
                    >
                      Assign
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Available Contractors
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Contractor</InputLabel>
            <Select
              value={selectedContractor}
              label="Select Contractor"
              onChange={(e) => setSelectedContractor(e.target.value)}
            >
              {contractors.map((contractor) => (
                <MenuItem key={contractor.id} value={contractor.id}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{contractor.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(contractor.specialties || []).join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedContractorData && (
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Contractor Details
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={selectedContractorData.name}
                    secondary={`Specialties: ${(selectedContractorData.specialties || []).join(', ')}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <PhoneIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Phone"
                    secondary={selectedContractorData.phone}
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <EmailIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Email"
                    secondary={selectedContractorData.email}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Hourly Rate"
                    secondary={`$${selectedContractorData.hourlyRate}/hour`}
                  />
                </ListItem>
              </List>
            </Box>
          )}

          <TextField
            label="Assignment Notes"
            value={assignmentNotes}
            onChange={(e) => setAssignmentNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Add any specific instructions or notes for the contractor..."
          />
        </Box>

        {currentAssignment && (
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Assignment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assigned to: {currentAssignment.contractorName || contractors.find(c => c.id === currentAssignment.contractorId)?.name || 'Unknown'}
            </Typography>
            {currentAssignment.assignmentNotes && (
              <Typography variant="body2" color="text.secondary">
                Notes: {currentAssignment.assignmentNotes}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Assigned: {currentAssignment.assignedAt ? new Date(currentAssignment.assignedAt._seconds * 1000).toLocaleString() : 'Unknown'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {currentAssignment && (
          <Button 
            onClick={handleUnassign} 
            color="error" 
            disabled={loading}
          >
            Unassign
          </Button>
        )}
        <Button 
          onClick={handleAssign} 
          variant="contained" 
          disabled={!selectedContractor || loading}
        >
          {currentAssignment ? 'Reassign' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractorAssignment; 